/**
 * « Prouver » (porteur, 06/10/2026) : trois boutons - E-mail / fonction 2 /
 * fonction 3. Seul « E-mail » existe (docs/prouver-email-cdc.md) : envoyer
 * un e-mail avec des PDF en pieces jointes et recevoir un Journal de preuve de
 * preuve d'envoi cachete et horodate eIDAS. Facture des la creation.
 */
import { useCallback, useEffect, useState } from "react";

import { api, type Expediteurs, type ModeleMessage, type ModePreuve, type PreuveEnvoi, type StatutAdresse } from "../api";
import { EditeurHtml, nettoyer } from "../components/EditeurHtml";
import { Chargement, Erreur, Vide, date, montant, telecharger } from "../components/ui";

const PIECES_MAX = 10;
const TAILLE_MAX_DEFAUT = 10 * 1024 * 1024;   // le devis donne celle du client (lot 3)

const STATUTS: Record<StatutAdresse, { texte: string; classe: string }> = {
  ACCEPTEE_SMTP: { texte: "Acceptée par le serveur", classe: "pill--ok" },
  REJET_DEFINITIF: { texte: "Adresse rejetée", classe: "pill--err" },
  INDETERMINE: { texte: "Indéterminé", classe: "pill--muted" },
  CATCH_ALL: { texte: "Domaine accepte-tout", classe: "pill--pending" },
  BOITE_PLEINE: { texte: "Boîte pleine", classe: "pill--pending" },
  REFUS_EXPEDITEUR: { texte: "Expéditeur refusé", classe: "pill--err" },
  EN_REPRISE: { texte: "Nouvel essai en cours", classe: "pill--pending" },
};

function StatutEnvoi({ p }: { p: PreuveEnvoi }) {
  if (!p.statut) return <span className="pill pill--pending">Envoi en cours</span>;
  const s = STATUTS[p.statut];
  return <span className={`pill ${s.classe}`}>{s.texte}</span>;
}

const OFFRES: { mode: ModePreuve; libelle: string; horodatage: string }[] = [
  { mode: "adobe", libelle: "Email Adobe", horodatage: "horodaté par un service reconnu par Adobe Acrobat" },
  { mode: "eidas", libelle: "Email eIDAS", horodatage: "horodaté eIDAS (horodatage qualifié)" },
];

export function Prouver() {
  const [ouvert, setOuvert] = useState<ModePreuve | null>(null);
  const [devis, setDevis] = useState<{ total_minor: number; currency_code: string; taille_max_octets: number } | null>(null);
  const [destinataire, setDestinataire] = useState("");
  const [nomExpediteur, setNomExpediteur] = useState("");
  const [objet, setObjet] = useState("");
  const [corps, setCorps] = useState("");
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [fait, setFait] = useState(false);
  const [liste, setListe] = useState<PreuveEnvoi[] | null>(null);
  const [modeles, setModeles] = useState<ModeleMessage[]>([]);
  const [modeleId, setModeleId] = useState("");
  const [nomModele, setNomModele] = useState("");
  const [modeleFait, setModeleFait] = useState<string | null>(null);
  const [expediteurs, setExpediteurs] = useState<Expediteurs | null>(null);
  const [expediteurId, setExpediteurId] = useState("");

  const chargerExpediteurs = useCallback(async () => {
    setExpediteurs(await api.expediteurs());
  }, []);

  const chargerModeles = useCallback(async () => {
    setModeles((await api.modelesMessage()).modeles);
  }, []);

  const appliquerModele = (id: string) => {
    setModeleId(id);
    setModeleFait(null);
    const m = modeles.find((x) => x.id === id);
    if (!m) return;
    setObjet(m.objet);
    setCorps(m.corps);
    setNomModele(m.nom);
  };

  const enregistrerModele = async () => {
    setErreur(null);
    try {
      const m = await api.enregistrerModele({ nom: nomModele.trim(), objet, corps: nettoyer(corps) });
      await chargerModeles();
      setModeleId(m.id);
      setModeleFait(`Modèle « ${m.nom} » enregistré.`);
    } catch (e) {
      setErreur(e);
    }
  };

  const supprimerModele = async () => {
    const m = modeles.find((x) => x.id === modeleId);
    if (!m || !window.confirm(`Supprimer le modèle « ${m.nom} » ?`)) return;
    setErreur(null);
    try {
      await api.supprimerModele(m.id);
      setModeleId("");
      await chargerModeles();
      setModeleFait(`Modèle « ${m.nom} » supprimé.`);
    } catch (e) {
      setErreur(e);
    }
  };

  const recharger = useCallback(async () => {
    try {
      setListe((await api.preuvesEnvoi()).preuves);
    } catch (e) {
      setErreur(e);
    }
  }, []);
  useEffect(() => {
    void recharger();
  }, [recharger]);

  // Tant qu'une enveloppe attend, la liste est relue : la file d'attente
  // (08/10/2026) remet l'e-mail et constitue le Journal de preuve.
  const enAttente = (liste ?? []).some((p) => p.etat === "en_attente");
  useEffect(() => {
    if (!enAttente) return;
    const minuteur = window.setInterval(() => void recharger(), 5000);
    return () => window.clearInterval(minuteur);
  }, [enAttente, recharger]);

  const taille = fichiers.reduce((s, f) => s + f.size, 0);
  const tailleMax = devis?.taille_max_octets ?? TAILLE_MAX_DEFAUT;
  const tropGros = taille > tailleMax || fichiers.length > PIECES_MAX;
  const pret = destinataire.trim() && nomExpediteur.trim() && objet.trim() && fichiers.length > 0 && !tropGros;

  const envoyer = async () => {
    if (!pret || !ouvert) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await api.envoyerAvecPreuve({ destinataire: destinataire.trim(), objet, corps: nettoyer(corps),
                                    nom_expediteur: nomExpediteur, expediteur_id: expediteurId, mode: ouvert },
                                  fichiers);
      setFait(true);
      setDestinataire("");
      setObjet("");
      setCorps("");
      setModeleId("");
      setFichiers([]);
      await recharger();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <div className="actions actions--split" style={{ margin: "0 0 1rem" }}>
        <div>
          <h1 className="page-title">Prouver</h1>
          <p className="page-lead" style={{ margin: 0 }}>
            Envoyer un document et en garder la preuve, scellée et horodatée.
          </p>
        </div>
        <div className="actions" style={{ margin: 0 }}>
          {OFFRES.map((o) => (
            <button key={o.mode} type="button" className={ouvert === o.mode ? "btn btn--primary" : "btn btn--secondary"}
                    onClick={() => {
                      setOuvert(o.mode); setFait(false); setErreur(null); setDevis(null);
                      // Relu a chaque clic : un prix change dans l'Admin doit s'afficher sans recharger la page.
                      void api.devisPreuveEnvoi(o.mode).then(setDevis, () => setDevis(null));
                      void chargerModeles().catch(setErreur);
                      void chargerExpediteurs().catch(setErreur);
                    }}>
              {o.libelle}
            </button>
          ))}
          <button type="button" className="btn btn--secondary" disabled title="À venir">Fonction 2</button>
          <button type="button" className="btn btn--secondary" disabled title="À venir">Fonction 3</button>
        </div>
      </div>

      {ouvert && (
        <div className="card stack" style={{ marginBottom: "1rem" }}>
          <h2 className="panneau__titre">{OFFRES.find((o) => o.mode === ouvert)?.libelle} : e-mail avec preuve d'envoi</h2>
          <p className="muted" style={{ margin: 0 }}>
            L'e-mail part avec vos PDF en pièces jointes ; chaque page porte le numéro d'enveloppe. Le Journal de
            preuve, disponible ici, établit l'envoi et la réponse du serveur du destinataire, scellé et{" "}
            {OFFRES.find((o) => o.mode === ouvert)?.horodatage}. Il ne prouve pas la lecture.
          </p>
          <div className="field">
            <label className="field-label" htmlFor="pe-dest">Destinataire</label>
            <input id="pe-dest" className="input" type="email" value={destinataire}
                   onChange={(e) => setDestinataire(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pe-nom">Nom de l'expéditeur affiché</label>
            <input id="pe-nom" className="input" maxLength={120} value={nomExpediteur}
                   onChange={(e) => setNomExpediteur(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pe-expediteur">Adresse d'expédition</label>
            <select id="pe-expediteur" className="select" value={expediteurId}
                    onChange={(e) => setExpediteurId(e.target.value)}>
              <option value="">Adresse Sign365, à votre nom</option>
              {(expediteurs?.adresses ?? []).filter((a) => a.utilisable).map((a) => (
                <option key={a.id} value={a.id}>{a.adresse}</option>
              ))}
            </select>
            <p className="muted" style={{ margin: "0.3rem 0 0" }}>
              Vos propres adresses s'ajoutent dans Paramètres → Adresses d'expéditeur.
            </p>
          </div>
          <div className="row">
            <div className="field">
              <label className="field-label" htmlFor="pe-modele">Modèle de message</label>
              <select id="pe-modele" className="select" value={modeleId} onChange={(e) => appliquerModele(e.target.value)}>
                <option value="">— texte libre —</option>
                {modeles.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
              </select>
            </div>
            {modeleId && (
              <div className="field" style={{ alignSelf: "end" }}>
                <button type="button" className="btn btn--link" onClick={() => void supprimerModele()}>
                  Supprimer ce modèle
                </button>
              </div>
            )}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pe-objet">Objet</label>
            <input id="pe-objet" className="input" maxLength={500} value={objet}
                   onChange={(e) => setObjet(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="pe-corps">Message</label>
            <EditeurHtml id="pe-corps" valeur={corps} onChange={setCorps} />
          </div>
          <div className="row">
            <div className="field">
              <label className="field-label" htmlFor="pe-nom-modele">Enregistrer l'objet et le message comme modèle</label>
              <input id="pe-nom-modele" className="input" maxLength={120} placeholder="nom du modèle"
                     value={nomModele} onChange={(e) => { setNomModele(e.target.value); setModeleFait(null); }} />
            </div>
            <div className="field" style={{ alignSelf: "end" }}>
              <button type="button" className="btn btn--secondary" disabled={!nomModele.trim()}
                      onClick={() => void enregistrerModele()}>
                {modeles.some((m) => m.nom === nomModele.trim()) ? "Remplacer le modèle" : "Enregistrer le modèle"}
              </button>
            </div>
          </div>
          {modeleFait && <div className="alert alert--success" role="status">{modeleFait}</div>}
          <label className="dropzone">
            <input type="file" accept="application/pdf,.pdf" multiple style={{ display: "none" }}
                   onChange={(e) => { setFichiers(Array.from(e.target.files ?? [])); setFait(false); }} />
            {fichiers.length ? <strong>{fichiers.map((f) => f.name).join(", ")}</strong>
              : <strong>Choisissez les PDF à joindre</strong>}
          </label>
          <p className="muted" style={{ margin: 0 }}>
            {PIECES_MAX} documents et {tailleMax / (1024 * 1024)} Mo au total au maximum
            {fichiers.length > 0 && ` — sélection : ${(taille / (1024 * 1024)).toFixed(1)} Mo`}.
          </p>
          {tropGros && <div className="alert alert--error">Trop de documents ou taille totale dépassée.</div>}
          <p style={{ margin: 0 }}>
            {devis ? <>Coût : <strong>{montant(devis.total_minor, devis.currency_code)}</strong>, débité à l'envoi.</>
              : "Tarif en cours de chargement ou non configuré."}
          </p>
          <Erreur erreur={erreur} />
          {fait && <div className="alert alert--info">E-mail envoyé. Le Journal de preuve sera disponible ci-dessous
            dès que le serveur du destinataire aura répondu.</div>}
          <div className="actions">
            <button type="button" className="btn btn--primary" disabled={!pret || envoi} onClick={() => void envoyer()}>
              {envoi ? "Envoi en cours…" : "Envoyer avec preuve"}
            </button>
          </div>
        </div>
      )}


      <div className="card">
        {liste === null ? <Chargement /> : liste.length === 0 ? <Vide>Aucun envoi.</Vide> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Destinataire</th><th>Objet</th><th>Statut</th><th>Coût</th><th>Le</th><th>Conservé jusqu'au</th><th /></tr>
              </thead>
              <tbody>
                {liste.map((p) => (
                  <tr key={p.id}>
                    <td>{p.destinataire}</td>
                    <td>{p.objet}</td>
                    <td><StatutEnvoi p={p} /></td>
                    <td>{montant(p.amount_minor, p.currency_code)}</td>
                    <td>{date(p.created_at, true)}</td>
                    <td>{date(p.peremption_le)}{p.prolongations > 0 && <span className="muted"> (prolongé)</span>}</td>
                    <td className="num">
                      {p.etat === "constitue" ? (
                        <button type="button" className="btn btn--link"
                                onClick={() => void api.journalEnvoi(p.id)
                                  .then((b) => telecharger(b, `journal-de-preuve-${p.id}.pdf`))}>
                          Journal de preuve
                        </button>
                      ) : <span className="muted">en attente</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
